import styled from "@emotion/styled"
import { Button, Tab, Tabs } from "@mui/material"
import { Station } from "api"
import { AppType, useAppType } from "contexts/AppTypeProvider"
import { LocationContext } from "contexts/LocationProvider"
import Image from "next/image"
import Link from "next/link"
import React, { useContext } from "react"
import { trackStationInfoMap } from "reusable/ChargeGTM"
import { LocationIcon, Mobile, MoreIcon, NewText } from "ui"
import { getLogoByName } from "./logo"

const StyledContainer = styled(Tabs)`
border: 1px solid #153e694d;

height: 100%;
& .MuiTabs-scroller{
position:relative;
}
& .MuiTabs-flexContainer{
  position:absolute;
width:100%;
display: flex;
align-items:stretch;
padding-right: 24px;
flex-direction: column;
overflow-y:scroll;
position: absolute;
top: 0;
left: 0;
bottom: 0;
}
${(props) => props.theme.breakpoints.down("md")} {
  height: 540px;
    padding-right: 0px;
}
`
const StyledItem = styled.div<{ $selected: boolean }>`
background: ${(props) =>
    props.$selected ? "#153E690A" : "none"};
width: 100%;
cursor: pointer;
border: none;
display: flex;
text-align: start;
padding: 7px 10px;
border-bottom: 1px solid #153e694d;
flex-direction: column;
${(props) => props.theme.breakpoints.up("md")} {
  padding: 5px 30.25px;
}
${(props) => props.theme.breakpoints.down("sm")} {
  > div {
    &:nth-child(2) {
      width: 100%;
    }
  }
}
`

const StyledItemLogo = styled.div`
border-radius: 8px;
position: relative;
width: 60px;
height: 60px;
min-width: 60px;
${(props) => props.theme.breakpoints.up("md")} {
}
${(props) => props.theme.breakpoints.up("lg")} {
  width: 80px;
  height: 80px;
}
`

const StyledItemText = styled.div`
display: flex;
flex-direction: column;
font-weight: 500;
${(props) => props.theme.breakpoints.down("sm")} {
  justify-content: center;
}
`

const StyledEmptyDiv = styled.div`
padding: 24px 20px;
${(props) => props.theme.breakpoints.up("md")} {
  padding: 16px 50.25px;
}
`

const StyledItemTop = styled.div`
display: flex;
align-items: center;
gap: 24px;
`

const StyledBottomDiv = styled.div`
display: flex;
justify-content: space-between;
width: 100%;
margin-left: 64px;
padding-top: 16px;
`

const StyledButtonDiv = styled.div`
display: flex;
align-items: center;
`

const StyledButton = styled(Button)`
color: #0050c9;
font-size: 14px;
border: none;
outline: none;
background-color: transparent;
padding: 0px;
`
const StyledDestination = styled.div`
display: flex;
align-items: center;
svg {
  path {
    stroke: #0050c9;
  }
}
a {
  color: #0050c9;
  text-decoration: none;
}
`

const StyledTab = styled(Tab)`
padding:0;
width:100%;
max-width:none;
:focus-visible{
  outline:1px solid black;
}
`

type TabProps = {
  it: Station;
  selected: boolean
  id: number
  handleOpen: () => void
}


function TabComp({ it, selected, handleOpen }: TabProps) {
  return <StyledItem
    $selected={selected}
  >
    <StyledItemTop>
      <StyledItemLogo>
        <Image src={getLogoByName(it.name, it.metaData)} fill alt="פז" />
      </StyledItemLogo>
      <StyledItemText>
        <NewText
          desktopFontSize={16}
          desktopLineHeight="22.6px"
          component="p"
          desktopFontWeight="600"
          largeDesktopFontSize={24}
          largeDesktopLineHeight="32.6px"
          mobileFontSize={20}
          mobileLineHeight={"28px"}
        >
          {it.name}
        </NewText>
        <NewText
          desktopFontSize={12}
          desktopLineHeight="18px"
          largeDesktopFontSize={16}
          largeDesktopLineHeight="24px"
          component="p"
          mobileFontSize={14}
          mobileLineHeight={"20px"}
        >
          {it.address}, {it.city}
        </NewText>
      </StyledItemText>
    </StyledItemTop>
    <Mobile>
      <StyledBottomDiv>
        <StyledDestination>
          <LocationIcon />
          <Link
            href={`https://waze.com/ul?ll=${it.geoLocation.latitude},${it.geoLocation.longitude}`}
            target="blank"
          >
            ניווט ליעד
          </Link>
        </StyledDestination>
        <StyledButtonDiv onClick={handleOpen}>
          <StyledButton>מידע נוסף</StyledButton>
          <MoreIcon />
        </StyledButtonDiv>
      </StyledBottomDiv>
    </Mobile>
  </StyledItem>
}

type Props = {
  list: Station[]
}

function ItemsList({ list }: Props) {
  const { selectedLocation, setSelectedLocation, setMobileDetails } =
    useContext(LocationContext)
  const { appType } = useAppType()
  const handleOpen = () => {
    setMobileDetails(true)
  }
  // eslint-disable-next-line no-unreachable
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    const station = list.find(li => li.id === newValue)
    if (station) {
      setSelectedLocation(station)
      if (appType === AppType.Charge) {
        trackStationInfoMap(station.name)
      }
    }
  }
  return (
    <>
      <StyledContainer
        onChange={handleChange}
        value={selectedLocation?.id || 0}
        selectionFollowsFocus
        orientation="vertical"
      >
        {list.length > 0 ? (
          list.map((it, key) => (
            <StyledTab
              key={key}
              value={it.id}
              disableRipple={true}
              id={`location_item_${it.id.toString()}`}
              label={<TabComp id={it.id}
                handleOpen={handleOpen} it={it}
                selected={selectedLocation?.id === it.id}

              />}
              tabIndex={selectedLocation?.id === it.id || key === 0 ? 0 : -1}
              aria-posinset={key + 1}
              aria-setsize={list.length}
              onKeyDown={(e) => {
                if (e.key === "Tab") {
                  const selectedDetail = document?.getElementById(`selectedDetail_${it.id.toString()}`);
                  if (selectedDetail) {
                    selectedDetail.focus();
                  }
                }

                // Handle left/right arrows
                if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
                  e.preventDefault();
                  // Get all tabs
                  const tabs = document.querySelectorAll('[role="tab"]');
                  const tabsArray = Array.from(tabs);
                  const currentIndex = tabsArray.findIndex(tab => tab === e.currentTarget);

                  // Calculate next index with wrap-around
                  let nextIndex;
                  if (e.key === "ArrowRight") {
                    nextIndex = currentIndex === 0 ? tabsArray.length - 1 : currentIndex - 1;
                  } else {
                    nextIndex = currentIndex === tabsArray.length - 1 ? 0 : currentIndex + 1;
                  }

                  // Focus next tab
                  (tabsArray[nextIndex] as HTMLElement).focus();
                }

                // Prevent default for Tab key
                if (e.key === "Tab") {
                  e.preventDefault();
                }
              }}
            />
          ))
        ) : (
          <StyledEmptyDiv>
            <NewText
              component={"p"}
              desktopFontSize={28}
              desktopFontWeight={400}
              desktopLineHeight={"40px"}
              mobileFontSize={18}
              mobileLineHeight={"24px"}
            >
              לא נמצאו תוצאות
            </NewText>
          </StyledEmptyDiv>
        )
        }
      </StyledContainer >
    </>
  )
}
export default ItemsList
